import { useContext, useEffect, useMemo, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'
import Select from 'react-select'
import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'

import UserContext from '../../context/User/User'
import {
  deletePromoterSalesReportSubscription,
  getOrgEventsForReport,
  listPromoterSalesReportSubscriptions,
  savePromoterSalesReportSubscription,
  sendPromoterSalesReportTestEmail,
} from '../../utilities/api'
import { checkPermission } from '../../utilities/helpers'
import AuthService from '../../utilities/services/auth.service'
import { NoPermissionsContainer } from '../NoPermissionsContainer'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const DEFAULT_FORM = {
  id: null,
  name: '',
  eventUuids: [],
  recipients: [],
  schedule: {
    enabled: true,
    frequency: 'daily',
    dayOfWeek: 1,
    hour: 8,
    minute: 0,
    timezone: 'America/New_York',
  },
  exportFormat: 'xlsx',
}
const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

function toTimeValue(hour, minute) {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function errorMessage(error, fallback) {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    fallback
  )
}

export default function PromoterSalesReportsWrapper({ organization }) {
  const { orgPermissions } = useContext(UserContext)
  const [reports, setReports] = useState([])
  const [events, setEvents] = useState([])
  const [form, setForm] = useState(DEFAULT_FORM)
  const [recipientInput, setRecipientInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [working, setWorking] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const reportPermissionSlug = ORGANIZATION_PERMISSION_SLUGS.VIEW_ORDER_LISTINGS
  const hasPermission =
    orgPermissions?.length > 0 &&
    (checkPermission(AuthService.getPermissions(), reportPermissionSlug) ||
      orgPermissions.some(
        (permission) =>
          (permission?.slug ?? permission?.attributes?.slug ?? permission) ===
          reportPermissionSlug
      ))
  const eventOptions = useMemo(
    () =>
      events.map((event) => ({
        value: event.uuid,
        label: `${event.name} — ${new Date(event.start).toLocaleDateString()}`,
      })),
    [events]
  )

  const loadData = async () => {
    if (!organization?.uuid || !hasPermission) return
    setLoading(true)
    setError('')
    try {
      const [reportsResponse, eventsResponse] = await Promise.all([
        listPromoterSalesReportSubscriptions(organization.uuid),
        getOrgEventsForReport(organization.uuid),
      ])
      setReports(reportsResponse.data?.subscriptions || [])
      setEvents(eventsResponse.data || [])
    } catch (requestError) {
      setError(errorMessage(requestError, 'Unable to load promoter reports.'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    // Organization and permission changes require a fresh, scoped list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.uuid, hasPermission])

  const resetForm = () => {
    setForm(DEFAULT_FORM)
    setRecipientInput('')
    setError('')
    setMessage('')
  }

  const loadReport = (report) => {
    setForm({
      ...DEFAULT_FORM,
      ...report,
      schedule: { ...DEFAULT_FORM.schedule, ...(report.schedule || {}) },
    })
    setRecipientInput('')
    setError('')
    setMessage('')
  }

  const addRecipient = (rawValue = recipientInput) => {
    const candidates = String(rawValue)
      .split(/[\s,;]+/)
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean)
    const invalid = candidates.find((email) => !EMAIL_PATTERN.test(email))
    if (invalid) {
      setError(`Enter a valid email address: ${invalid}`)
      return
    }
    setForm((current) => ({
      ...current,
      recipients: [...new Set([...current.recipients, ...candidates])],
    }))
    setRecipientInput('')
    setError('')
  }

  const removeRecipient = (email) => {
    setForm((current) => ({
      ...current,
      recipients: current.recipients.filter((value) => value !== email),
    }))
  }

  const payload = {
    ...form,
    organizationUUID: organization?.uuid,
  }

  const validate = () => {
    if (!form.name.trim()) return 'Report name is required.'
    if (!form.eventUuids.length) return 'Select at least one event.'
    if (!form.recipients.length) return 'Add at least one recipient.'
    return ''
  }

  const save = async () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }
    setWorking(true)
    setError('')
    setMessage('')
    try {
      await savePromoterSalesReportSubscription(payload)
      const successMessage = form.id
        ? 'Promoter report updated.'
        : 'Promoter report saved.'
      resetForm()
      setMessage(successMessage)
      await loadData()
    } catch (requestError) {
      setError(errorMessage(requestError, 'Unable to save promoter report.'))
    } finally {
      setWorking(false)
    }
  }

  const sendTest = async () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }
    setWorking(true)
    setError('')
    setMessage('')
    try {
      await sendPromoterSalesReportTestEmail(payload)
      setMessage('Test report queued for delivery.')
    } catch (requestError) {
      setError(errorMessage(requestError, 'Unable to send the test report.'))
    } finally {
      setWorking(false)
    }
  }

  const remove = async (report) => {
    if (!window.confirm(`Delete "${report.name}"?`)) return
    setWorking(true)
    setError('')
    try {
      await deletePromoterSalesReportSubscription(report.id, organization.uuid)
      if (form.id === report.id) resetForm()
      await loadData()
    } catch (requestError) {
      setError(errorMessage(requestError, 'Unable to delete promoter report.'))
    } finally {
      setWorking(false)
    }
  }

  if (!hasPermission) return <NoPermissionsContainer />

  return (
    <section className="spacer-sm access-control-report access-control-report--v2">
      <header className="section-header-sm report-page-header">
        <div className="section-heading">
          <h1>Promoter ticket sales reports</h1>
        </div>
        <p className="section-header-desc">
          Email combined ticket-sales summaries for selected events on a daily
          or weekly schedule, broken out by pricing level.
        </p>
      </header>

      {error && <Alert variant="danger">{error}</Alert>}
      {message && (
        <Alert className="mb-3" variant="success">
          {message}
        </Alert>
      )}

      <Row className="g-3">
        <Col lg={4}>
          <Card body className="card--md h-100">
            <Stack
              direction="horizontal"
              className="justify-content-between mb-3"
            >
              <h5 className="mb-0">Saved reports</h5>
              <Button size="sm" variant="outline-primary" onClick={resetForm}>
                New
              </Button>
            </Stack>
            {loading ? (
              <span className="text-muted">Loading…</span>
            ) : reports.length ? (
              <Stack gap={2}>
                {reports.map((report) => (
                  <Card body key={report.id} className="p-3">
                    <button
                      type="button"
                      className="btn btn-link text-start p-0 fw-semibold"
                      onClick={() => loadReport(report)}
                    >
                      {report.name}
                    </button>
                    <div className="small text-muted mt-1">
                      {report.eventUuids.length} event
                      {report.eventUuids.length === 1 ? '' : 's'} ·{' '}
                      {report.schedule.frequency}
                    </div>
                    <Button
                      size="sm"
                      variant="link"
                      className="text-danger p-0 mt-2"
                      disabled={working}
                      onClick={() => remove(report)}
                    >
                      Delete
                    </Button>
                  </Card>
                ))}
              </Stack>
            ) : (
              <span className="text-muted">No scheduled reports yet.</span>
            )}
          </Card>
        </Col>

        <Col lg={8}>
          <Card body className="card--md">
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group controlId="promoterReportName">
                  <Form.Label>Report name</Form.Label>
                  <Form.Control
                    value={form.name}
                    maxLength={80}
                    onChange={(event) =>
                      setForm({ ...form, name: event.target.value })
                    }
                    placeholder="Weekly promoter sales"
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Label>Events</Form.Label>
                <Select
                  inputId="promoterReportEvents"
                  aria-label="Events"
                  isMulti
                  options={eventOptions}
                  value={eventOptions.filter((option) =>
                    form.eventUuids.includes(option.value)
                  )}
                  onChange={(options) =>
                    setForm({
                      ...form,
                      eventUuids: (options || []).map((option) => option.value),
                    })
                  }
                  placeholder="Select one or more events"
                />
              </Col>
              <Col xs={12}>
                <Form.Label>Promoter recipients</Form.Label>
                <Stack
                  direction="horizontal"
                  gap={2}
                  className="flex-wrap mb-2"
                >
                  {form.recipients.map((email) => (
                    <Badge bg="light" text="dark" key={email}>
                      {email}{' '}
                      <button
                        type="button"
                        className="btn-close ms-1"
                        aria-label={`Remove ${email}`}
                        onClick={() => removeRecipient(email)}
                      />
                    </Badge>
                  ))}
                </Stack>
                <Form.Control
                  aria-label="Promoter recipients"
                  type="email"
                  value={recipientInput}
                  placeholder="promoter@example.com"
                  onChange={(event) => setRecipientInput(event.target.value)}
                  onBlur={() => recipientInput && addRecipient()}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ',') {
                      event.preventDefault()
                      addRecipient()
                    }
                  }}
                />
                <Form.Text>Add an address with Enter or a comma.</Form.Text>
              </Col>
              <Col md={6}>
                <Form.Group controlId="promoterReportFrequency">
                  <Form.Label>Frequency</Form.Label>
                  <Form.Select
                    value={form.schedule.frequency}
                    onChange={(event) =>
                      setForm({
                        ...form,
                        schedule: {
                          ...form.schedule,
                          frequency: event.target.value,
                        },
                      })
                    }
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {form.schedule.frequency === 'weekly' && (
                <Col md={6}>
                  <Form.Group controlId="promoterReportDayOfWeek">
                    <Form.Label>Day of week</Form.Label>
                    <Form.Select
                      value={form.schedule.dayOfWeek}
                      onChange={(event) =>
                        setForm({
                          ...form,
                          schedule: {
                            ...form.schedule,
                            dayOfWeek: Number(event.target.value),
                          },
                        })
                      }
                    >
                      {WEEKDAYS.map((day, index) => (
                        <option value={index} key={day}>
                          {day}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
              <Col md={6}>
                <Form.Group controlId="promoterReportDeliveryTime">
                  <Form.Label>Delivery time</Form.Label>
                  <Form.Control
                    type="time"
                    value={toTimeValue(
                      form.schedule.hour,
                      form.schedule.minute
                    )}
                    onChange={(event) => {
                      const [hour, minute] = event.target.value
                        .split(':')
                        .map(Number)
                      setForm({
                        ...form,
                        schedule: { ...form.schedule, hour, minute },
                      })
                    }}
                  />
                  <Form.Text>America/New_York timezone</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="promoterReportExportFormat">
                  <Form.Label>Attachment format</Form.Label>
                  <Form.Select
                    value={form.exportFormat}
                    onChange={(event) =>
                      setForm({ ...form, exportFormat: event.target.value })
                    }
                  >
                    <option value="xlsx">Excel (.xlsx)</option>
                    <option value="csv">CSV</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Check
                  type="switch"
                  label="Scheduled delivery enabled"
                  checked={form.schedule.enabled}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      schedule: {
                        ...form.schedule,
                        enabled: event.target.checked,
                      },
                    })
                  }
                />
                <Form.Text>
                  Daily reports cover the previous day. Weekly reports cover the
                  previous Monday–Sunday.
                </Form.Text>
              </Col>
              <Col xs={12}>
                <Stack direction="horizontal" gap={2} className="flex-wrap">
                  <Button disabled={working} onClick={save}>
                    {form.id ? 'Update report' : 'Save report'}
                  </Button>
                  <Button
                    variant="outline-primary"
                    disabled={working}
                    onClick={sendTest}
                  >
                    Send test email
                  </Button>
                  {form.id && (
                    <Button variant="outline-light" onClick={resetForm}>
                      Cancel editing
                    </Button>
                  )}
                </Stack>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </section>
  )
}
